import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ListResponse } from '@verantos-dev/types-common';

import { authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';
import type { ProjectArtifactEntry } from './types';

interface DeleteAnalysisInput {
  projectId: string;
  analysisProfileId: string;
}

/**
 * Delete an analysis profile (and its version history) from the artifacts
 * page. Only the creator may delete; a run in progress returns 409, surfaced
 * to the caller.
 */
export const useDeleteAnalysisMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteAnalysisInput>({
    mutationFn: ({ projectId, analysisProfileId }) =>
      authFetch<void>(
        `/analyses/${encodeURIComponent(projectId)}` +
          `/analysis-profile/${encodeURIComponent(analysisProfileId)}`,
        { method: 'DELETE' }
      ),
    onSuccess: (_data, { projectId, analysisProfileId }) => {
      // Optimistically drop the deleted row from the manifest cache so the list
      // updates instantly, instead of waiting for the refetch round-trip below.
      const manifestKey = projectPageKeys.artifactsForProject(projectId);
      queryClient.setQueryData<ListResponse<ProjectArtifactEntry>>(manifestKey, (old) =>
        old ? { ...old, data: old.data.filter((e) => e.id !== analysisProfileId) } : old
      );
      // The analysis's PROJECT_ARTIFACT row is detached server-side, so reconcile
      // the manifest (and refresh the activity feed) against the server.
      queryClient.invalidateQueries({ queryKey: manifestKey });
      queryClient.invalidateQueries({
        queryKey: projectPageKeys.activityForProject(projectId),
      });
    },
  });
};
